/* Supply › Purchase › Debit Note (Purchase Return)
 * Return goods to a supplier against an original purchase. The operator looks
 * up the purchase by its invoice number, which prefills the returnable lines
 * (batch + on-hand qty); posting decrements stock (RETURN_OUT) and debits the
 * supplier. See GRN_NOTES_API_SPEC.md §3. */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { cbFmtINR, cbFmtNum, cbFmtTime, cbFmtDateTime } from "../supplyData";
import {
  Button,
  Pill,
  Input,
  Select,
  Table,
  Tr,
  Td,
  SectionHeader,
  ToolbarBar,
  DateRangeFilter,
  KpiBox,
  Eyebrow,
  LoadingState,
  ErrorState,
  IconPlus,
  IconX,
  IconCheck,
  IconEdit,
  IconChevRight,
  IconBook,
  IconDownload,
  KV,
} from "../supplyKit";
import notesService from "./notesService";
import supplyService from "../../../Services/supplyService";
import { isPharmacyHead } from "../../../Services/warehouseContext";
import { downloadDebitNotePdf } from "./debitNoteDocument";

const REASONS = ["EXPIRY", "DAMAGE", "RATE_DIFF", "SHORT_SUPPLY", "WRONG_ITEM", "SCHEME", "OTHER"];
const today = () => new Date().toISOString().slice(0, 10);

/* ============================ List ============================ */
const DebitNote = () => {
  const [creating, setCreating] = useState(false);
  /* A pharmacy head sees every warehouse's notes, so the list must say which
     warehouse each belongs to and let them narrow to one. A regular user only
     ever sees their own, where the column and filter would be dead weight. */
  const isAdmin = isPharmacyHead();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  /* No warehouse filter: the list is scoped to the store selected app-wide, and
     notesService sends that automatically. */
  const [filters, setFilters] = useState({ q: "", status: "", fromDate: "", toDate: "" });
  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const [pageState, setPageState] = useState({ page: 0, size: 20, totalPages: 0, totalElements: 0 });
  const setPageField = (k, v) => setPageState((s) => ({ ...s, [k]: v, ...(k === "size" ? { page: 0 } : {}) }));
  const [selected, setSelected] = useState(null); // noteId shown in the detail panel
  const [editId, setEditId] = useState(null); // noteId open in the edit drawer

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const params = { page: pageState.page, size: pageState.size };
    if (filters.q) params.search = filters.q.trim();
    if (filters.status) params.status = filters.status;
    if (filters.fromDate) params.fromDate = filters.fromDate;
    if (filters.toDate) params.toDate = filters.toDate;
    notesService.purchaseReturns
      .list(params)
      .then((res) => {
        if (!alive) return;
        setRows(res.content || []);
        setPageState((s) => ({ ...s, totalPages: res.totalPages || 0, totalElements: res.totalElements || 0 }));
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        const msg = e?.message || "Could not load purchase returns.";
        setError(msg);
        toast.error(msg);
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [filters, pageState.page, pageState.size, reloadKey]);

  useEffect(() => {
    setPageState((s) => (s.page === 0 ? s : { ...s, page: 0 }));
  }, [filters]);

  if (creating) {
    return (
      <DebitNoteDrawer
        onClose={() => setCreating(false)}
        onSaved={() => {
          setCreating(false);
          setReloadKey((k) => k + 1);
        }}
      />
    );
  }

  if (editId != null) {
    return (
      <DebitNoteDetailDrawer
        id={editId}
        editable
        onClose={() => setEditId(null)}
        onSaved={() => {
          setEditId(null);
          setReloadKey((k) => k + 1);
        }}
      />
    );
  }

  const start = pageState.totalElements === 0 ? 0 : pageState.page * pageState.size + 1;
  const end = Math.min(pageState.totalElements, (pageState.page + 1) * pageState.size);
  const selectedNote = rows.find((n) => n.noteId === selected) || null;

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader
        title="Purchase Returns"
        sub="Return goods to a supplier against an original purchase invoice (raises a debit note)"
        actions={
          <Button variant="primary" icon={<IconPlus size={14} />} onClick={() => setCreating(true)}>
            New Purchase Return
          </Button>
        }
      />

      <ToolbarBar>
        <Input
          value={filters.q}
          onChange={(e) => setF("q", e.target.value)}
          placeholder="Note no / invoice / supplier…"
          style={{ width: 260, maxWidth: "100%" }}
        />
        <Select value={filters.status} onChange={(e) => setF("status", e.target.value)}>
          <option value="">All statuses</option>
          {["DRAFT", "POSTED", "CANCELLED"].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
        <DateRangeFilter
          from={filters.fromDate}
          to={filters.toDate}
          onFromChange={(v) => setF("fromDate", v)}
          onToChange={(v) => setF("toDate", v)}
        />
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {cbFmtNum(pageState.totalElements)} notes
        </div>
      </ToolbarBar>

      {loading ? (
        <LoadingState label="Loading purchase returns…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <div
          className={`grid grid-cols-1 ${selectedNote ? "lg:grid-cols-[1fr_440px]" : ""}`}
          style={{
            display: "grid",
            gap: 12,
            alignItems: "start",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
            <Table
              columns={[
                { label: "Note #" },
                { label: "Date & time" },
                ...(isAdmin ? [{ label: "Warehouse" }] : []),
                { label: "Supplier" },
                { label: "Against invoice" },
                { label: "Reason" },
                { label: "Items", align: "right" },
                { label: "Net", align: "right" },
                { label: "Status" },
                { label: "", width: 24 },
              ]}
            >
              {rows.length === 0 && (
                <tr>
                  <Td colSpan={isAdmin ? 10 : 9} style={{ textAlign: "center", color: "var(--fg-muted)", height: 80 }}>
                    No purchase returns yet.
                  </Td>
                </tr>
              )}
              {rows.map((n) => (
                <Tr
                  key={n.noteId}
                  selected={selected === n.noteId}
                  onClick={() => setSelected(selected === n.noteId ? null : n.noteId)}
                >
                  <Td mono style={{ fontWeight: 600 }}>
                    {n.noteNumber || `#${n.noteId}`}
                  </Td>
                  <Td
                    mono
                    title={
                      [
                        n.createdAt ? `Created ${cbFmtDateTime(n.createdAt)}` : "",
                        n.postedAt ? `Posted ${cbFmtDateTime(n.postedAt)}` : "",
                      ]
                        .filter(Boolean)
                        .join(" \u00b7 ") || undefined
                    }
                  >
                    <div>{String(n.noteDate).slice(0, 10)}</div>
                    {cbFmtTime(n.createdAt) && (
                      <div style={{ fontSize: 10, color: "var(--fg-muted)" }}>{cbFmtTime(n.createdAt)}</div>
                    )}
                  </Td>
                  {isAdmin && <Td>{n.warehouseName || n.warehouseId || "—"}</Td>}
                  <Td>{n.supplierName || n.supplierId}</Td>
                  <Td mono style={{ color: "var(--fg-muted)" }}>
                    {n.linkedDocNumber}
                  </Td>
                  <Td>
                    <Pill variant="neutral">{n.reason || "—"}</Pill>
                  </Td>
                  <Td mono right>
                    {cbFmtNum(n.itemCount)}
                  </Td>
                  <Td mono right style={{ fontWeight: 600 }}>
                    {cbFmtINR(n.netAmount)}
                  </Td>
                  <Td>
                    <Pill variant={n.status === "POSTED" ? "green" : n.status === "CANCELLED" ? "red" : "neutral"} dot>
                      {n.status}
                    </Pill>
                  </Td>
                  <Td>
                    <IconChevRight size={12} style={{ color: "var(--fg-muted)" }} />
                  </Td>
                </Tr>
              ))}
            </Table>

            <ToolbarBar>
              <Select value={pageState.size} onChange={(e) => setPageField("size", Number(e.target.value))}>
                {[20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </Select>
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                  {start}–{end} of {cbFmtNum(pageState.totalElements)}
                </span>
                <Button size="sm" disabled={pageState.page <= 0} onClick={() => setPageField("page", Math.max(0, pageState.page - 1))}>
                  ← Prev
                </Button>
                <Button
                  size="sm"
                  disabled={pageState.page + 1 >= pageState.totalPages}
                  onClick={() => setPageField("page", pageState.page + 1)}
                >
                  Next →
                </Button>
              </div>
            </ToolbarBar>
          </div>

          {selectedNote && (
            <DebitNoteDetailPanel
              id={selectedNote.noteId}
              fallback={selectedNote}
              onClose={() => setSelected(null)}
              onEdit={(id) => setEditId(id)}
              onChanged={() => {
                setReloadKey((k) => k + 1);
                setSelected(null);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

/* ============================ Create ============================ */
/* Warning confirmation popup for destructive, non-undoable actions inside the
   drawer. z-index sits above the drawer so it is not clipped by it. */
const ConfirmDialog = ({ title, message, confirmLabel = "Confirm", onConfirm, onCancel }) => (
  <div style={{ position: "fixed", inset: 0, zIndex: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
    <div onClick={onCancel} style={{ position: "absolute", inset: 0, background: "var(--bg-overlay)" }} />
    <div
      role="alertdialog"
      aria-modal="true"
      className="cb-animate-in"
      style={{
        position: "relative",
        width: "min(420px, 92vw)",
        background: "var(--bg-panel)",
        border: "1px solid var(--border-default)",
        borderRadius: 8,
        boxShadow: "0 12px 40px rgba(13,17,27,0.22)",
        padding: "18px 20px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            flexShrink: 0,
            background: "var(--amber-50, var(--bg-sunken))",
            color: "var(--amber-700, var(--red-700))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 15,
            fontWeight: 700,
          }}
        >
          !
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--fg-strong)" }}>{title}</div>
      </div>
      <div style={{ fontSize: 12, color: "var(--fg-muted)", lineHeight: 1.5 }}>{message}</div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="primary" onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  </div>
);

/* Ways to find the original purchase. Invoice number resolves to exactly one
   purchase; a medicine search can match several, so those modes show a picker. */
const SEARCH_MODES = [
  { id: "invoice", label: "Invoice no", placeholder: "e.g. HP-F006425", width: 220 },
  { id: "medicineName", label: "Medicine name", placeholder: "e.g. Nupenta", width: 240 },
  { id: "medicineCode", label: "Medicine ID", placeholder: "e.g. 11020204", width: 200 },
];

const DebitNoteDrawer = ({ onClose, onSaved }) => {
  const [searchMode, setSearchMode] = useState("invoice");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [looking, setLooking] = useState(false);
  const [purchase, setPurchase] = useState(null);
  /* Candidate purchases from a medicine search, shown for the operator to pick.
     Empty for an invoice search, which resolves to one purchase directly. */
  const [candidates, setCandidates] = useState([]);
  const [confirmClearLines, setConfirmClearLines] = useState(false);
  /* The medicine search the candidates came from — used to add only that
     medicine's lines rather than the whole invoice. */
  const [lastSearch, setLastSearch] = useState(null);
  const [lines, setLines] = useState([]);
  const [reason, setReason] = useState("EXPIRY");
  const [noteDate, setNoteDate] = useState(today());
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  const mode = SEARCH_MODES.find((m) => m.id === searchMode) || SEARCH_MODES[0];

  /* The search term means something different in each mode, so it and any
     pending results are always cleared. The picked lines are only discarded when
     crossing between invoice mode (which builds a return from one invoice) and
     the medicine modes (which accumulate) — switching between "medicine name"
     and "medicine ID" is just another way to find the next line, so the return
     built so far carries over. */
  const changeMode = (id) => {
    const crossesInvoiceBoundary = (id === "invoice") !== (searchMode === "invoice");
    setSearchMode(id);
    setInvoiceNo("");
    setCandidates([]);
    if (crossesInvoiceBoundary) {
      setPurchase(null);
      setLines([]);
    }
  };

  /* Identity of a returnable line: the lot it will be returned from, scoped to
     the purchase it came from. Used to stop a repeated search re-adding lines
     that are already in the working set. */
  const lineKey = (l) => `${l.src?.id}|${l.inventoryId ?? `${l.medicineCode}-${l.batchNo}`}`;

  /* Stamp each line with the purchase it belongs to, so the working set can span
     several invoices and still be grouped back per invoice on save. */
  const tag = (p, ls) =>
    ls.map((l) => ({
      ...l,
      src: {
        id: p.id,
        invoiceNumber: p.supplierInv,
        supplierId: p.supplier,
        supplierName: p.supplierName,
        warehouseId: p.warehouseId,
        wh: p.wh,
        date: p.date,
      },
    }));

  /* Add one matched medicine entry (already tagged with its source purchase) to
     the working set. Results stay on screen so several can be taken in a row. */
  const addEntry = (entry) => {
    let added = 0;
    setLines((prev) => {
      if (prev.some((l) => lineKey(l) === lineKey(entry))) return prev;
      added = 1;
      return [...prev, entry];
    });
    if (!added) toast.info("That batch is already in this return.");
  };

  const addAllEntries = () => {
    let added = 0;
    setLines((prev) => {
      const seen = new Set(prev.map(lineKey));
      const fresh = candidates.filter((l) => !seen.has(lineKey(l)));
      added = fresh.length;
      return [...prev, ...fresh];
    });
    toast[added ? "success" : "info"](
      added ? `${added} batch${added !== 1 ? "es" : ""} added.` : "Those batches are already in this return."
    );
  };

  /* Blank every entered return quantity, keeping the lines themselves. "Reset
     all" sits above a table of quantity boxes, so it resets those — it used to
     drop the whole picked set, which meant re-fetching the invoice to correct a
     single typo. Removing a line is what the per-row × is for. */
  const clearQuantities = () => {
    setLines((prev) => prev.map((l) => ({ ...l, returnQty: "" })));
    setConfirmClearLines(false);
  };

  const removeLine = (key) => setLines((prev) => prev.filter((l) => lineKey(l) !== key));

  const fetchInvoice = async () => {
    if (looking) return;
    const term = invoiceNo.trim();
    if (!term) return;
    setLooking(true);
    setCandidates([]);
    /* Only an invoice search starts a fresh return. A medicine search is meant to
       be run repeatedly, so it must leave the already-picked lines alone —
       clearing here is what made earlier picks disappear on the next search. */
    if (searchMode === "invoice") {
      setPurchase(null);
      setLines([]);
    }
    try {
      if (searchMode === "invoice") {
        /* Invoice search behaves exactly as before: it replaces the working set. */
        const { purchase: p, lines: ls } = await notesService.lookupPurchaseByInvoice(term);
        setPurchase(p);
        setLines(tag(p, ls));
        if (!ls.length) toast.warn("Purchase found, but it has no line items to return.");
      } else {
        /* Results are the matching medicine lines themselves — batch, expiry,
           on-hand, rate — each showing which invoice it came from. */
        const { groups, scanned, total } = await notesService.lookupReturnableLinesByMedicine(
          searchMode === "medicineCode" ? { medicineCode: term } : { medicineName: term }
        );
        const entries = groups.flatMap((g) => tag(g.purchase, g.lines));
        setLastSearch({ mode: searchMode, term });
        setCandidates(entries);
        if (!entries.length) {
          toast.warn(`No returnable batch found for “${term}”.`);
        } else if (total > scanned) {
          toast.info(`Showing matches from the ${scanned} most recent of ${total} purchases.`);
        }
      }
    } catch (e) {
      toast.error(e?.message || "Could not fetch the purchase.");
    } finally {
      setLooking(false);
    }
  };

  /* Clamped to what is actually returnable, so an over-return cannot be typed at
     all rather than being flagged after the fact. `max` on the input only guards
     the spinner — typing or pasting walks straight past it.
     Blank stays blank (an empty box is "not returning this line", not zero). */
  const setQty = (idx, val) =>
    setLines((prev) =>
      prev.map((l, i) => {
        if (i !== idx) return l;
        if (val === "") return { ...l, returnQty: "" };
        const n = Number(val);
        if (!Number.isFinite(n)) return l;
        const cap = Number(l.availableQty) || 0;
        return { ...l, returnQty: String(Math.min(Math.max(n, 0), cap)) };
      })
    );

  const computed = useMemo(() => {
    let taxable = 0,
      cgst = 0,
      sgst = 0,
      count = 0;
    lines.forEach((l) => {
      const c = notesService.calcLine(l);
      if (c.qty > 0) {
        taxable += c.taxableAmount;
        cgst += c.cgstAmount;
        sgst += c.sgstAmount;
        count += 1;
      }
    });
    const net = taxable + cgst + sgst;
    return { taxable, cgst, sgst, net, count };
  }, [lines]);

  const overReturn = lines.some((l) => (Number(l.returnQty) || 0) > l.availableQty);
  const canSave = computed.count > 0 && !overReturn && !saving;

  /* Distinct source invoices among the lines that actually carry a quantity —
     one debit note will be raised per entry here. */
  const noteGroups = useMemo(() => {
    const seen = new Map();
    lines.forEach((l) => {
      if (!((Number(l.returnQty) || 0) > 0)) return;
      const key = String(l.src?.id);
      if (!seen.has(key)) seen.set(key, { id: l.src?.id, label: l.src?.invoiceNumber || `#${l.src?.id}` });
    });
    return [...seen.values()];
  }, [lines]);

  /* PurchaseReturnRequest carries a single purchaseInvoiceId, so lines picked
     across several purchases become one debit note per source invoice. Only
     lines with a quantity are included, and each group's netAmount is summed
     from its own lines (not the screen total). */
  const buildPayloads = (status) => {
    const groups = new Map();
    lines.forEach((l) => {
      if (!((Number(l.returnQty) || 0) > 0)) return;
      const key = String(l.src?.id);
      if (!groups.has(key)) groups.set(key, { src: l.src, items: [], net: 0 });
      const g = groups.get(key);
      const c = notesService.calcLine(l);
      g.items.push({
        inventoryId: l.inventoryId,
        quantity: c.qty,
        purchasePrice: Number(l.purchasePrice) || 0,
        discountAmount: c.discountAmount,
        gstPercentage: c.gstPercentage,
      });
      g.net += c.lineTotal;
    });
    return [...groups.values()].map((g) => ({
      returnDate: noteDate,
      purchaseInvoiceId: g.src.id,
      supplierId: g.src.supplierId != null ? Number(g.src.supplierId) : undefined,
      warehouseId: g.src.warehouseId,
      reason,
      remarks: remarks || "",
      status,
      netAmount: round2(g.net),
      items: g.items,
    }));
  };

  const save = async (post) => {
    if (!canSave) return;
    setSaving(true);
    const payloads = buildPayloads("DRAFT");
    let done = 0;
    try {
      /* Sequential on purpose — each note must be created (and posted) before the
         next, so a mid-way failure leaves a clear, reportable boundary rather
         than an unknown mix of successes. */
      for (const payload of payloads) {
        const created = await notesService.purchaseReturns.create(payload);
        const id = created?.returnId ?? created?.noteId ?? created?.id;
        if (post && id != null) await notesService.purchaseReturns.post(id);
        done += 1;
      }
      toast.success(
        payloads.length === 1
          ? post
            ? "Purchase return posted."
            : "Purchase return saved as draft."
          : `${payloads.length} debit notes ${post ? "posted" : "saved as draft"} — one per purchase invoice.`
      );
      onSaved();
    } catch (e) {
      /* Say what actually happened: some notes may already exist. */
      const detail = done > 0 ? ` ${done} of ${payloads.length} were already created — check the list before retrying.` : "";
      toast.error(`${e?.message || "Could not save the debit note."}${detail}`);
      if (done > 0) onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader
        title="New Purchase Return"
        sub="Find the original purchase by invoice number, medicine name or medicine ID, then enter return quantities"
        actions={
          <Button icon={<IconX size={14} />} onClick={onClose}>
            Cancel
          </Button>
        }
      />

      {/* Purchase lookup — by invoice, medicine name or medicine ID */}
      <ToolbarBar>
        <span style={{ fontSize: 11, color: "var(--fg-muted)", fontWeight: 600 }}>Search by</span>
        <Select value={searchMode} onChange={(e) => changeMode(e.target.value)} style={{ width: 150, maxWidth: "100%" }}>
          {SEARCH_MODES.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </Select>
        <Input
          value={invoiceNo}
          onChange={(e) => setInvoiceNo(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && fetchInvoice()}
          placeholder={mode.placeholder}
          style={{ width: mode.width, maxWidth: "100%" }}
        />
        <Button variant="primary" onClick={fetchInvoice} disabled={looking || !invoiceNo.trim()}>
          {looking ? "Searching…" : searchMode === "invoice" ? "Fetch" : "Search"}
        </Button>
        {searchMode !== "invoice" && lines.length > 0 && (
          <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>
            Search again to add lines from another purchase.
          </span>
        )}
        {searchMode === "invoice" && purchase && (
          <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>
            {purchase.supplierName} · {purchase.wh} · invoice dated {purchase.date}
          </span>
        )}
      </ToolbarBar>

      {/* Medicine-search results — the matching batches, ready to add */}
      {candidates.length > 0 && (
        <div style={{ border: "1px solid var(--border-default)", borderRadius: 6, overflow: "hidden" }}>
          <div
            style={{
              padding: "6px 12px",
              background: "var(--bg-subtle)",
              borderBottom: "1px solid var(--border-default)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <Eyebrow>
              {candidates.length} batch{candidates.length !== 1 ? "es" : ""} of
              {lastSearch?.term ? ` \u201c${lastSearch.term}\u201d` : " this medicine"} available to return
            </Eyebrow>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Button onClick={addAllEntries} disabled={looking}>
                Add all
              </Button>
              <button
                onClick={() => setCandidates([])}
                style={{ border: 0, background: "transparent", color: "var(--fg-link)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
              >
                Dismiss
              </button>
            </div>
          </div>
          <div style={{ overflowX: "auto" }} className="cb-scroll">
            <Table
              columns={[
                { label: "Medicine" },
                { label: "Batch" },
                { label: "Expiry" },
                { label: "On hand", align: "right" },
                { label: "Rate", align: "right" },
                { label: "GST %", align: "right" },
                { label: "Invoice" },
                { label: "Supplier" },
                { label: "" },
              ]}
            >
              {candidates.map((e) => {
                const inSet = lines.some((l) => lineKey(l) === lineKey(e));
                const noLot = e.inventoryId == null || e.availableQty <= 0;
                return (
                  <Tr key={lineKey(e)}>
                    <Td>
                      <span style={{ fontWeight: 600, color: "var(--fg-strong)" }}>
                        {e.medicineName || e.medicineCode}
                      </span>
                      <span style={{ marginLeft: 6, fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                        {e.medicineCode}
                      </span>
                    </Td>
                    <Td mono>{e.batchNo}</Td>
                    <Td mono>{e.expiryDate}</Td>
                    {/* v2 says why a batch cannot go back (already returned, no
                        stock left, GRN cancelled) — show that rather than making
                        the operator infer it from a zero. */}
                    <Td mono right style={{ color: noLot ? "var(--red-700)" : undefined }}>
                      {cbFmtNum(e.availableQty)}
                      {noLot && e.notReturnableReason && (
                        <div style={{ fontSize: 10, fontFamily: "var(--font-sans)", color: "var(--fg-muted)" }}>
                          {e.notReturnableReason}
                        </div>
                      )}
                      {!noLot && e.alreadyReturnedQty > 0 && (
                        <div style={{ fontSize: 10, fontFamily: "var(--font-sans)", color: "var(--fg-muted)" }}>
                          {cbFmtNum(e.alreadyReturnedQty)} already returned
                        </div>
                      )}
                    </Td>
                    <Td mono right>{cbFmtINR(e.purchasePrice)}</Td>
                    <Td mono right>{cbFmtNum(e.gstPercentage)}%</Td>
                    <Td mono title={e.src?.date ? `Invoice dated ${e.src.date}` : undefined}>
                      {e.src?.invoiceNumber || `#${e.src?.id}`}
                    </Td>
                    <Td>{e.src?.supplierName || "\u2014"}</Td>
                    <Td>
                      <Button
                        variant={inSet ? undefined : "primary"}
                        onClick={() => addEntry(e)}
                        disabled={inSet || noLot}
                        title={
                          noLot
                            ? e.notReturnableReason || "Nothing left to return on this batch"
                            : inSet
                            ? "Already in this return"
                            : undefined
                        }
                      >
                        {inSet ? "Added" : "Add"}
                      </Button>
                    </Td>
                  </Tr>
                );
              })}
            </Table>
          </div>
        </div>
      )}

      {lines.length === 0 ? (
        looking ? (
          <LoadingState label="Fetching purchase…" />
        ) : (
          candidates.length === 0 && (
            <div
              style={{
                padding: "28px 16px",
                textAlign: "center",
                color: "var(--fg-muted)",
                fontSize: 13,
                border: "1px dashed var(--border-default)",
                borderRadius: 6,
              }}
            >
              {searchMode === "invoice" ? (
                <>
                  Enter the supplier's purchase invoice number and click <strong>Fetch</strong> to load returnable lines.
                </>
              ) : (
                <>
                  Search by {mode.label.toLowerCase()} to find the purchases containing it, then add their lines.
                  You can search repeatedly to build a return across several purchases.
                </>
              )}
            </div>
          )
        )
      ) : (
        <>
          {/* Note header */}
          <ToolbarBar>
            <span style={{ fontSize: 11, color: "var(--fg-muted)", fontWeight: 600 }}>Reason</span>
            <Select value={reason} onChange={(e) => setReason(e.target.value)}>
              {REASONS.map((r) => (
                <option key={r} value={r}>
                  {r.replace(/_/g, " ")}
                </option>
              ))}
            </Select>
            <span style={{ fontSize: 11, color: "var(--fg-muted)", fontWeight: 600 }}>Note date</span>
            <Input type="date" value={noteDate} onChange={(e) => setNoteDate(e.target.value)} style={{ width: 150, maxWidth: "100%" }} />
            <Input
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Remarks (optional)"
              style={{ width: 240, maxWidth: "100%" }}
            />
          </ToolbarBar>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" style={{ display: "grid", gap: 10 }}>
            <KpiBox label="Lines to return" value={cbFmtNum(computed.count)} variant="ink" />
            <KpiBox label="Taxable" value={cbFmtINR(computed.taxable)} variant="ink" />
            <KpiBox label="GST (CGST+SGST)" value={cbFmtINR(computed.cgst + computed.sgst)} variant="ink" />
            <KpiBox label="Return value" value={cbFmtINR(computed.net)} variant="red" />
          </div>

          {noteGroups.length > 1 && (
            <div
              style={{
                padding: "8px 12px",
                background: "var(--ink-50)",
                borderRadius: 4,
                fontSize: 11,
                color: "var(--ink-700)",
                lineHeight: 1.5,
              }}
            >
              These lines span <strong>{noteGroups.length} purchase invoices</strong>. A debit note is raised against a
              single invoice, so saving will create <strong>{noteGroups.length} separate debit notes</strong> — one per
              invoice ({noteGroups.map((g) => g.label).join(", ")}).
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <Eyebrow>Returnable lines · enter quantity to return (≤ on-hand)</Eyebrow>
            {/* Enabled by entered quantities, not by picked lines — with nothing
                typed there is nothing to reset. */}
            <button
              onClick={() => setConfirmClearLines(true)}
              disabled={computed.count === 0}
              style={{
                border: 0,
                background: "transparent",
                color: computed.count ? "var(--red-700)" : "var(--fg-subtle, var(--fg-muted))",
                fontSize: 11,
                fontWeight: 600,
                cursor: computed.count ? "pointer" : "not-allowed",
              }}
            >
              Reset all
            </button>
          </div>

          {confirmClearLines && (
            <ConfirmDialog
              title={`Clear ${computed.count} entered quantit${computed.count !== 1 ? "ies" : "y"}?`}
              message="Every return quantity you have typed will be cleared. The lines stay on screen, so you can re-enter without fetching the invoice again."
              confirmLabel="Clear quantities"
              onConfirm={clearQuantities}
              onCancel={() => setConfirmClearLines(false)}
            />
          )}
          <Table
            columns={[
              { label: "Invoice" },
              { label: "Medicine" },
              { label: "Batch" },
              { label: "Expiry" },
              { label: "On hand", align: "right" },
              { label: "Rate", align: "right" },
              { label: "GST %", align: "right" },
              { label: "Return qty", align: "right" },
              { label: "Return value", align: "right" },
              { label: "" },
            ]}
          >
            {lines.map((l, i) => {
              const c = notesService.calcLine(l);
              const over = (Number(l.returnQty) || 0) > l.availableQty;
              const noLot = l.inventoryId == null || l.availableQty <= 0;
              return (
                <Tr key={lineKey(l)}>
                  <Td mono title={`${l.src?.supplierName || ""} · ${l.src?.date || ""}`}>
                    {l.src?.invoiceNumber || `#${l.src?.id}`}
                  </Td>
                  <Td>
                    <span style={{ fontWeight: 600, color: "var(--fg-strong)" }}>{l.medicineName || l.medicineCode}</span>
                    <span style={{ marginLeft: 6, fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                      {l.medicineCode}
                    </span>
                  </Td>
                  <Td mono>{l.batchNo}</Td>
                  <Td mono>{l.expiryDate}</Td>
                  <Td mono right style={{ color: noLot ? "var(--red-700)" : undefined }}>
                    {cbFmtNum(l.availableQty)}
                    {noLot && l.notReturnableReason && (
                      <div style={{ fontSize: 10, fontFamily: "var(--font-sans)", color: "var(--fg-muted)" }}>
                        {l.notReturnableReason}
                      </div>
                    )}
                    {!noLot && l.alreadyReturnedQty > 0 && (
                      <div style={{ fontSize: 10, fontFamily: "var(--font-sans)", color: "var(--fg-muted)" }}>
                        {cbFmtNum(l.alreadyReturnedQty)} already returned
                      </div>
                    )}
                  </Td>
                  <Td mono right>
                    {cbFmtINR(l.purchasePrice)}
                  </Td>
                  <Td mono right>
                    {cbFmtNum(l.gstPercentage)}%
                  </Td>
                  <Td right>
                    <input
                      type="number"
                      min={0}
                      max={l.availableQty}
                      value={l.returnQty}
                      disabled={noLot}
                      onChange={(e) => setQty(i, e.target.value)}
                      style={{
                        width: 80,
                        height: 26,
                        textAlign: "right",
                        border: `1px solid ${over ? "var(--red-500)" : "var(--border-default)"}`,
                        borderRadius: 4,
                        fontFamily: "var(--font-mono)",
                        fontSize: 12,
                        padding: "0 6px",
                      }}
                      title={noLot ? "No stock on hand for this batch" : undefined}
                    />
                  </Td>
                  <Td mono right style={{ fontWeight: 600 }}>
                    {cbFmtINR(c.lineTotal)}
                  </Td>
                  <Td>
                    <button
                      onClick={() => removeLine(lineKey(l))}
                      title="Remove this line from the return"
                      style={{
                        border: 0,
                        background: "transparent",
                        cursor: "pointer",
                        color: "var(--fg-muted)",
                        padding: 2,
                        display: "inline-flex",
                      }}
                    >
                      <IconX size={13} />
                    </button>
                  </Td>
                </Tr>
              );
            })}
          </Table>

          {overReturn && (
            <div style={{ fontSize: 12, color: "var(--red-700)", fontWeight: 600 }}>
              One or more return quantities exceed the on-hand stock.
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => save(false)} disabled={!canSave}>
              Save draft
            </Button>
            <Button variant="primary" onClick={() => save(true)} disabled={!canSave}>
              {saving ? "Posting…" : "Post return"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

/* ====================== View / Edit a single note ====================== */
/* Loads a purchase return (GET /{id}); read-only for non-drafts, editable for
   DRAFT (qty per line + reason + remarks) -> PATCH /{id}, with optional post. */
const DebitNoteDetailDrawer = ({ id, editable, onClose, onSaved }) => {
  const [note, setNote] = useState(null);
  const [lines, setLines] = useState([]);
  const [reason, setReason] = useState("EXPIRY");
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    notesService.purchaseReturns
      .get(id)
      .then((n) => {
        if (!alive) return;
        setNote(n);
        setReason(n.reason || "EXPIRY");
        setRemarks(n.remarks || "");
        setLines(
          (n.items || []).map((it) => {
            const qty = Number(it.quantity) || 0;
            const price = Number(it.purchasePrice) || 0;
            const gross = qty * price;
            return {
              inventoryId: it.inventoryId,
              medicineCode: it.medicineCode,
              medicineName: it.medicineName,
              batchNo: it.batchNo,
              expiryDate: it.expiryDate,
              purchasePrice: price,
              gstPercentage: Number(it.gstPercentage) || 0,
              discountPercentage: gross > 0 ? round2(((Number(it.discountAmount) || 0) / gross) * 100) : 0,
              returnQty: qty,
            };
          })
        );
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load the purchase return.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  /* Clamped to what is actually returnable, so an over-return cannot be typed at
     all rather than being flagged after the fact. `max` on the input only guards
     the spinner — typing or pasting walks straight past it.
     Blank stays blank (an empty box is "not returning this line", not zero). */
  const setQty = (idx, val) =>
    setLines((prev) =>
      prev.map((l, i) => {
        if (i !== idx) return l;
        if (val === "") return { ...l, returnQty: "" };
        const n = Number(val);
        if (!Number.isFinite(n)) return l;
        const cap = Number(l.availableQty) || 0;
        return { ...l, returnQty: String(Math.min(Math.max(n, 0), cap)) };
      })
    );

  const computed = useMemo(() => {
    let taxable = 0,
      cgst = 0,
      sgst = 0,
      count = 0;
    lines.forEach((l) => {
      const c = notesService.calcLine(l);
      if (c.qty > 0) {
        taxable += c.taxableAmount;
        cgst += c.cgstAmount;
        sgst += c.sgstAmount;
        count += 1;
      }
    });
    return { taxable, cgst, sgst, net: taxable + cgst + sgst, count };
  }, [lines]);

  const canSave = editable && note && computed.count > 0 && !saving;

  const buildPayload = (status) => ({
    returnDate: String(note.noteDate || today()).slice(0, 10),
    purchaseInvoiceId: note.linkedDocId,
    supplierId: note.supplierId != null ? Number(note.supplierId) : undefined,
    warehouseId: note.warehouseId,
    reason,
    remarks: remarks || "",
    status,
    netAmount: round2(computed.net),
    items: lines
      .filter((l) => (Number(l.returnQty) || 0) > 0)
      .map((l) => {
        const c = notesService.calcLine(l);
        return {
          inventoryId: l.inventoryId,
          quantity: c.qty,
          purchasePrice: Number(l.purchasePrice) || 0,
          discountAmount: c.discountAmount,
          gstPercentage: c.gstPercentage,
        };
      }),
  });

  const save = async (post) => {
    if (!canSave) return;
    setSaving(true);
    try {
      await notesService.purchaseReturns.update(id, buildPayload("DRAFT"));
      if (post) await notesService.purchaseReturns.post(id);
      toast.success(post ? "Purchase return posted." : "Draft updated.");
      onSaved();
    } catch (e) {
      toast.error(e?.message || "Could not save the purchase return.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader
        title={editable ? "Edit Purchase Return" : "Purchase Return"}
        sub={note ? `${note.noteNumber || `#${note.noteId}`} · against ${note.linkedDocNumber || "—"}` : "Loading…"}
        actions={
          <Button icon={<IconX size={14} />} onClick={onClose}>
            Close
          </Button>
        }
      />

      {loading ? (
        <LoadingState label="Loading purchase return…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => onClose()} />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" style={{ display: "grid", gap: 10 }}>
            <KpiBox label="Supplier" value={note.supplierName || note.supplierId || "—"} variant="ink" />
            <KpiBox label="Warehouse" value={note.warehouseName || note.warehouseId || "—"} variant="ink" />
            <KpiBox label="Status" value={note.status} variant={note.status === "POSTED" ? "green" : note.status === "CANCELLED" ? "red" : "ink"} />
            <KpiBox label="Return value" value={cbFmtINR(editable ? computed.net : note.netAmount)} variant="red" />
          </div>

          <ToolbarBar>
            <span style={{ fontSize: 11, color: "var(--fg-muted)", fontWeight: 600 }}>Reason</span>
            {editable ? (
              <Select value={reason} onChange={(e) => setReason(e.target.value)}>
                {REASONS.map((r) => (
                  <option key={r} value={r}>
                    {r.replace(/_/g, " ")}
                  </option>
                ))}
              </Select>
            ) : (
              <Pill variant="neutral">{note.reason || "—"}</Pill>
            )}
            <span style={{ fontSize: 11, color: "var(--fg-muted)", fontWeight: 600 }}>Date</span>
            <span style={{ fontSize: 12, fontFamily: "var(--font-mono)" }}>{String(note.noteDate).slice(0, 10)}</span>
            {editable ? (
              <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Remarks (optional)" style={{ width: 240, maxWidth: "100%" }} />
            ) : (
              note.remarks && <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>{note.remarks}</span>
            )}
          </ToolbarBar>

          <Table
            columns={[
              { label: "Medicine" },
              { label: "Batch" },
              { label: "Expiry" },
              { label: "Rate", align: "right" },
              { label: "GST %", align: "right" },
              { label: "Qty", align: "right" },
              { label: "Line value", align: "right" },
            ]}
          >
            {lines.map((l, i) => {
              const c = notesService.calcLine(l);
              return (
                <Tr key={`${l.medicineCode}-${l.batchNo}-${i}`}>
                  <Td>
                    <span style={{ fontWeight: 600, color: "var(--fg-strong)" }}>{l.medicineName || l.medicineCode}</span>
                    <span style={{ marginLeft: 6, fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                      {l.medicineCode}
                    </span>
                  </Td>
                  <Td mono>{l.batchNo}</Td>
                  <Td mono>{l.expiryDate}</Td>
                  <Td mono right>
                    {cbFmtINR(l.purchasePrice)}
                  </Td>
                  <Td mono right>
                    {cbFmtNum(l.gstPercentage)}%
                  </Td>
                  <Td right>
                    {editable ? (
                      <input
                        type="number"
                        min={0}
                        value={l.returnQty}
                        onChange={(e) => setQty(i, e.target.value)}
                        style={{
                          width: 80,
                          height: 26,
                          textAlign: "right",
                          border: "1px solid var(--border-default)",
                          borderRadius: 4,
                          fontFamily: "var(--font-mono)",
                          fontSize: 12,
                          padding: "0 6px",
                        }}
                      />
                    ) : (
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{cbFmtNum(l.returnQty)}</span>
                    )}
                  </Td>
                  <Td mono right style={{ fontWeight: 600 }}>
                    {cbFmtINR(c.lineTotal)}
                  </Td>
                </Tr>
              );
            })}
          </Table>

          {editable && (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button onClick={() => save(false)} disabled={!canSave}>
                {saving ? "Saving…" : "Save draft"}
              </Button>
              <Button variant="primary" onClick={() => save(true)} disabled={!canSave}>
                {saving ? "Posting…" : "Post return"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ================= Detail panel (GRN-style master/detail) ================= */
const panelTh = {
  padding: "5px 8px",
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--fg-muted)",
  textAlign: "left",
  borderBottom: "1px solid var(--border-default)",
  whiteSpace: "nowrap",
};
const panelTd = { padding: "6px 8px", borderBottom: "1px solid var(--border-subtle)", verticalAlign: "middle" };
const statusVariant = (s) => (s === "POSTED" ? "green" : s === "CANCELLED" ? "red" : "neutral");

const DebitNoteDetailPanel = ({ id, fallback, onClose, onEdit, onChanged }) => {
  const navigate = useNavigate();
  const [note, setNote] = useState(fallback || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    notesService.purchaseReturns
      .get(id)
      .then((n) => {
        if (!alive) return;
        setNote(n);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load the purchase return.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  const runWith = async (label, fn) => {
    setBusy(true);
    try {
      await fn();
      toast.success(label);
      onChanged();
    } catch (e) {
      toast.error(e?.message || "Action failed.");
    } finally {
      setBusy(false);
    }
  };
  const post = () => {
    if (!window.confirm("Post this purchase return? Stock will be decremented (RETURN_OUT) and the supplier debited.")) return;
    runWith("Purchase return posted.", () => notesService.purchaseReturns.post(id));
  };
  const cancel = () => {
    const reason = window.prompt("Reason for cancelling this purchase return?");
    if (reason == null) return;
    if (!reason.trim()) {
      toast.error("A cancellation reason is required.");
      return;
    }
    runWith("Purchase return cancelled.", () => notesService.purchaseReturns.cancel(id, reason.trim()));
  };
  const viewLedger = () => navigate("/inventory", { state: { tab: "movements", refId: id } });

  /* Debit note PDF — the document sent to the supplier. Rendered from whatever
     the panel is showing, so it always matches the detail on screen. */
  const [downloading, setDownloading] = useState(false);
  const downloadPdf = async () => {
    setDownloading(true);
    try {
      await downloadDebitNotePdf(note || fallback || {});
    } catch (e) {
      toast.error(e?.message || "Could not generate the debit note PDF.");
    } finally {
      setDownloading(false);
    }
  };

  const n = note || fallback || {};
  const items = Array.isArray(n.items) ? n.items : [];
  const status = n.status || "DRAFT";
  const between = { display: "flex", justifyContent: "space-between" };
  const mono = { fontFamily: "var(--font-mono)" };

  return (
    <aside
      className="lg:sticky lg:top-3 lg:max-h-[calc(100vh-120px)]"
      style={{
        border: "1px solid var(--border-default)",
        borderRadius: 6,
        background: "var(--bg-panel)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid var(--border-default)",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 700, color: "var(--fg-strong)" }}>
              {n.noteNumber || `#${n.noteId}`}
            </span>
            <Pill variant={statusVariant(status)} dot>
              {status}
            </Pill>
          </div>
          <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 4, lineHeight: 1.4 }}>
            {String(n.noteDate).slice(0, 10)} · {n.supplierName || n.supplierId} · invoice{" "}
            <span style={{ fontFamily: "var(--font-mono)" }}>{n.linkedDocNumber}</span>
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            width: 24,
            height: 24,
            border: 0,
            background: "transparent",
            cursor: "pointer",
            color: "var(--fg-muted)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconX size={14} />
        </button>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
        {error ? (
          <ErrorState message={error} onRetry={onClose} />
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <KV label="Supplier" value={n.supplierName || n.supplierId || "—"} />
              <KV label="Warehouse" value={n.warehouseName || n.warehouseId || "—"} mono small />
              <KV label="Reason" value={(n.reason || "—").replace(/_/g, " ")} />
              <KV label="Remarks" value={n.remarks || "—"} />
            </div>

            <div>
              <Eyebrow>Line items · {items.length}</Eyebrow>
              <div style={{ border: "1px solid var(--border-default)", borderRadius: 4, overflowX: "auto", marginTop: 6 }}>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: "var(--bg-subtle)" }}>
                      <th style={panelTh}>Medicine</th>
                      <th style={{ ...panelTh, textAlign: "right", width: 44 }}>Qty</th>
                      <th style={{ ...panelTh, textAlign: "right", width: 64 }}>Rate</th>
                      <th style={{ ...panelTh, textAlign: "right", width: 70 }}>₹</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && items.length === 0 ? (
                      <tr>
                        <td colSpan={4} style={{ ...panelTd, textAlign: "center", color: "var(--fg-muted)" }}>
                          Loading…
                        </td>
                      </tr>
                    ) : (
                      items.map((it, i) => (
                        <tr key={i}>
                          <td style={panelTd}>
                            <div style={{ fontWeight: 600, color: "var(--fg-strong)", fontSize: 11 }}>
                              {it.medicineName || it.medicineCode}
                            </div>
                            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--fg-muted)", marginTop: 2 }}>
                              {it.medicineCode} · B {it.batchNo} · Exp {it.expiryDate}
                            </div>
                          </td>
                          <td style={{ ...panelTd, textAlign: "right", fontFamily: "var(--font-mono)", fontWeight: 600 }}>
                            {cbFmtNum(it.quantity)}
                          </td>
                          <td style={{ ...panelTd, textAlign: "right", fontFamily: "var(--font-mono)" }}>
                            {cbFmtINR(it.purchasePrice)}
                          </td>
                          <td style={{ ...panelTd, textAlign: "right", fontFamily: "var(--font-mono)" }}>
                            {cbFmtINR(it.lineTotal)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ padding: "8px 10px", background: "var(--bg-subtle)", borderRadius: 4, display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>
              <div style={between}>
                <span>Taxable value</span>
                <span style={mono}>{cbFmtINR(n.taxableAmount)}</span>
              </div>
              <div style={{ ...between, color: "var(--fg-muted)" }}>
                <span>GST (CGST + SGST)</span>
                <span style={mono}>{cbFmtINR((Number(n.cgstAmount) || 0) + (Number(n.sgstAmount) || 0))}</span>
              </div>
              <div style={{ height: 1, background: "var(--border-subtle)", margin: "2px 0" }} />
              <div style={{ ...between, fontWeight: 700, color: "var(--fg-strong)" }}>
                <span>Total</span>
                <span style={mono}>{cbFmtINR(n.netAmount)}</span>
              </div>
            </div>

            {status === "DRAFT" && (
              <div style={{ padding: "10px 12px", background: "var(--ink-50)", borderRadius: 4, fontSize: 11, color: "var(--ink-700)", lineHeight: 1.5 }}>
                <strong>On posting:</strong> stock for {items.length} lot{items.length !== 1 ? "s" : ""} will be decremented (
                <Pill variant="red" style={{ fontSize: 9, height: 14, padding: "0 4px", verticalAlign: "baseline" }}>
                  RETURN_OUT
                </Pill>
                ) and the supplier payable reduced.
              </div>
            )}
          </>
        )}
      </div>

      {!error && (
        <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border-default)", display: "flex", gap: 8, flexWrap: "wrap" }}>
          {status === "DRAFT" ? (
            <>
              <Button style={{ flex: 1, justifyContent: "center" }} icon={<IconEdit size={14} />} onClick={() => onEdit(id)} disabled={busy}>
                Edit
              </Button>
              <Button style={{ flex: 1, justifyContent: "center" }} icon={<IconX size={14} />} onClick={cancel} disabled={busy}>
                Cancel
              </Button>
              <Button
                variant="primary"
                style={{ flex: 1.4, justifyContent: "center" }}
                icon={<IconCheck size={14} />}
                onClick={post}
                disabled={busy}
              >
                {busy ? "Working…" : "Post return"}
              </Button>
            </>
          ) : (
            /* POSTED or CANCELLED — the note is final, only the document and
               the ledger remain. */
            <>
              <Button
                style={{ flex: 1, justifyContent: "center" }}
                icon={<IconDownload size={14} />}
                onClick={downloadPdf}
                disabled={downloading || loading}
              >
                {downloading ? "Preparing…" : "Download PDF"}
              </Button>
              <Button style={{ flex: 1, justifyContent: "center" }} icon={<IconBook size={14} />} onClick={viewLedger}>
                View ledger
              </Button>
            </>
          )}
        </div>
      )}
    </aside>
  );
};

/* local rounding (kept out of render hot path) */
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export default DebitNote;
